'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const services = [
  {
    id: 'followers',
    emoji: '👥',
    title: 'Service Facebook',
    description: 'Augmentez votre audience avec des abonnés de qualité. Profils réels, livraison progressive pour une croissance naturelle.',
    features: ['Profils actifs & réels', 'Livraison en 24-48h', 'Garantie 30 jours', 'À partir de 2 000 XOF'],
    color: '#1877F2',
    bgColor: 'rgba(24,119,242,0.06)',
    borderColor: 'rgba(24,119,242,0.15)',
    badge: 'Le plus populaire',
    badgeColor: '#1877F2',
  },
  {
    id: 'tiktok',
    emoji: '🎵',
    title: 'Services TikTok',
    description: 'Boostez votre présence TikTok avec des abonnés, likes, vues et commentaires réels. Devenez viral rapidement.',
    features: ['Abonnés, Likes, Vues', 'Commentaires TikTok', 'Livraison rapide', 'À partir de 30 XOF'],
    color: '#2563EB',
    bgColor: 'rgba(37,99,235,0.06)',
    borderColor: 'rgba(37,99,235,0.2)',
    badge: '🎵 TikTok',
    badgeColor: '#2563EB',
    isTiktok: true,
  },
  {
    id: 'instagram',
    emoji: '📸',
    title: 'Service Instagram',
    description: 'Développez votre communauté Instagram avec des abonnés réels et actifs. Croissance naturelle et livraison progressive.',
    features: ['Profils réels & actifs', 'Livraison progressive', 'Garantie 30 jours', 'À partir de 240 XOF'],
    color: '#C13584',
    bgColor: 'rgba(193,53,132,0.08)',
    borderColor: 'rgba(193,53,132,0.25)',
    badge: '📸 Instagram',
    badgeColor: '#C13584',
    isInstagram: true,
  },
  {
    id: 'youtube',
    emoji: '▶️',
    title: 'Service YouTube',
    description: 'Boostez votre chaîne YouTube avec des abonnés, likes, vues et commentaires réels. Augmentez votre visibilité rapidement.',
    features: ['Abonnés, Likes, Vues', 'Commentaires YouTube', 'Livraison rapide', 'À partir de 80 XOF'],
    color: '#FF0000',
    bgColor: 'rgba(255,0,0,0.06)',
    borderColor: 'rgba(255,0,0,0.15)',
    badge: '▶️ YouTube',
    badgeColor: '#FF0000',
    isYoutube: true,
  },
];

export default function ServicesPreview() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-card').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 sm:py-28 px-4 sm:px-6" style={{ background: '#F4F6FB' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="section-label mb-5 mx-auto" style={{ display: 'inline-flex' }}>
            <Icon name="SparklesIcon" size={13} className="text-[#1877F2]" />
            Nos Services
          </div>
          <h2
            className="font-heading font-bold text-[#0D1B3E] mb-4"
            style={{ fontSize: 'clamp(1.8rem,4vw,3rem)', lineHeight: 1.15 }}
          >
            Tout ce dont vous avez besoin
            <br />
            <span className="gradient-text">pour dominer les réseaux sociaux</span>
          </h2>
          <p className="text-[#6B7280] max-w-xl mx-auto" style={{ fontSize: 15 }}>
            Des services Facebook, TikTok et Instagram adaptés à chaque besoin, avec paiement local en XOF via Orange Money, MTN MoMo ou Wave.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.map((svc) => (
            <div
              key={svc.id}
              className={`reveal-card service-card p-7 flex flex-col gap-5`}
              style={{
                opacity: 0,
                transform: 'translateY(30px)',
                transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1)',
                background: svc.isTiktok
                  ? 'linear-gradient(135deg,#0a0a0a 0%,#0a0a1a 100%)'
                  : svc.isInstagram
                  ? 'linear-gradient(135deg,#1a0a1a 0%,#2d0a2d 100%)'
                  : (svc as { isYoutube?: boolean }).isYoutube
                  ? 'white' :'white',
                border: svc.isTiktok
                  ? '1px solid rgba(37,99,235,0.25)'
                  : svc.isInstagram
                  ? '1px solid rgba(193,53,132,0.3)'
                  : (svc as { isYoutube?: boolean }).isYoutube
                  ? '1px solid rgba(255,0,0,0.15)'
                  : undefined,
              }}
            >
              {/* Top */}
              <div className="flex items-start justify-between">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
                  style={{
                    background: svc.isInstagram
                      ? 'linear-gradient(135deg,rgba(131,58,180,0.2),rgba(253,29,29,0.15),rgba(247,119,55,0.15))'
                      : (svc as { isYoutube?: boolean }).isYoutube
                      ? 'rgba(255,0,0,0.15)'
                      : svc.bgColor,
                    border: `1px solid ${svc.borderColor}`,
                  }}
                >
                  {svc.emoji}
                </div>
                <span
                  className="text-xs font-bold px-3 py-1 rounded-full"
                  style={{
                    background: svc.isInstagram
                      ? 'linear-gradient(135deg,rgba(131,58,180,0.2),rgba(253,29,29,0.15))'
                      : (svc as { isYoutube?: boolean }).isYoutube
                      ? 'rgba(255,0,0,0.15)'
                      : svc.bgColor,
                    color: svc.badgeColor,
                    border: `1px solid ${svc.borderColor}`,
                  }}
                >
                  {svc.badge}
                </span>
              </div>

              {/* Content */}
              <div>
                <h3
                  className="font-heading font-bold text-xl mb-2"
                  style={{ color: svc.isTiktok || svc.isInstagram ? 'white' : '#0D1B3E' }}
                >
                  {svc.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: svc.isTiktok || svc.isInstagram ? 'rgba(255,255,255,0.6)' : '#6B7280' }}
                >
                  {svc.description}
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-2">
                {svc.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-center gap-2 text-sm"
                    style={{ color: svc.isTiktok || svc.isInstagram ? 'rgba(255,255,255,0.8)' : '#374151' }}
                  >
                    <div
                      className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: svc.isInstagram
                          ? 'rgba(193,53,132,0.2)'
                          : (svc as { isYoutube?: boolean }).isYoutube
                          ? 'rgba(255,0,0,0.1)'
                          : svc.bgColor,
                      }}
                    >
                      <Icon name="CheckIcon" size={10} style={{ color: svc.color }} />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                href={svc.id === 'followers' ? '/facebook' : svc.id === 'tiktok' ? '/tiktok' : svc.id === 'instagram' ? '/instagram' : '/youtube'}
                className="mt-auto flex items-center gap-2 font-semibold text-sm transition-all group"
                style={{ color: svc.isInstagram ? '#E1306C' : svc.color, textDecoration: 'none' }}
              >
                Commander maintenant
                <Icon
                  name="ArrowRightIcon"
                  size={15}
                  className="group-hover:translate-x-1 transition-transform"
                  style={{ color: svc.isInstagram ? '#E1306C' : svc.color }}
                />
              </Link>
            </div>
          ))}
        </div>

        {/* View all CTA */}
        <div className="text-center mt-10">
          <Link
            href="/services"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '14px 32px',
              borderRadius: 50,
              background: 'linear-gradient(135deg,#0D1B3E 0%,#162550 100%)',
              color: 'white',
              fontFamily: 'Plus Jakarta Sans,sans-serif',
              fontWeight: 700,
              fontSize: 15,
              textDecoration: 'none',
              boxShadow: '0 4px 20px rgba(13,27,62,0.2)',
              transition: 'all 0.3s',
            }}
          >
            Voir tous les tarifs
            <Icon name="ArrowRightIcon" size={16} className="text-white" />
          </Link>
        </div>
      </div>
    </section>
  );
}